/**
 * Recent ingestion runs table for the Integrations page.
 *
 * Brand pattern: navy header with white caption-type column titles
 * (matches frontend-standards/patterns.md § Navy data-table header).
 * Read-only — no row interactions in this commit.
 */
import React from "react"

import { IngestionRun } from "../../api/integrations"
import { THEME } from "../../config/theme"
import { formatTimestamp } from "../../utils/format"

const STATUS_COLORS: Record<string, string> = {
  success: THEME.colors.semantic.success,
  failed: THEME.colors.semantic.danger,
  running: THEME.colors.navy,
}

const formatVendorName = (name: string): string =>
  name
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")

function formatDuration(startedAt: string, completedAt: string | null): string {
  if (!completedAt) return "—"
  const start = new Date(startedAt).getTime()
  const end = new Date(completedAt).getTime()
  if (Number.isNaN(start) || Number.isNaN(end)) return "—"
  const seconds = Math.round((end - start) / 1000)
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  const remSeconds = seconds % 60
  return `${minutes}m ${remSeconds}s`
}

const headerCellStyle: React.CSSProperties = {
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  fontSize: 12,
  fontWeight: 500,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  color: THEME.colors.white,
  textAlign: "left",
  backgroundColor: THEME.auditTable.headerBackground,
  borderRight: "1px solid rgba(255, 255, 255, 0.1)",
  borderBottom: `1px solid ${THEME.auditTable.borderColor}`,
}

const cellStyle: React.CSSProperties = {
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  fontSize: 12,
  color: THEME.colors.neutral.light.textPrimary,
  borderRight: `1px solid ${THEME.auditTable.borderColor}`,
  borderBottom: `1px solid ${THEME.auditTable.borderColor}`,
  verticalAlign: "top",
}

interface Props {
  runs: IngestionRun[]
  loading: boolean
}

const RunHistoryTable: React.FC<Props> = ({ runs, loading }) => {
  if (loading && runs.length === 0) {
    return (
      <div
        style={{
          padding: THEME.spacing.xl,
          textAlign: "center",
          color: THEME.colors.neutral.light.textSecondary,
          backgroundColor: THEME.colors.neutral.light.background,
          borderRadius: THEME.borderRadius.md,
          border: `1px solid ${THEME.auditTable.borderColor}`,
          fontFamily: THEME.fonts.primary,
        }}
      >
        Loading runs…
      </div>
    )
  }

  if (runs.length === 0) {
    return (
      <div
        style={{
          padding: THEME.spacing.xl,
          textAlign: "center",
          color: THEME.colors.neutral.light.textSecondary,
          backgroundColor: THEME.colors.neutral.light.background,
          borderRadius: THEME.borderRadius.md,
          border: `1px solid ${THEME.auditTable.borderColor}`,
          fontFamily: THEME.fonts.primary,
        }}
      >
        No ingestion runs yet.
      </div>
    )
  }

  return (
    <div
      style={{
        width: "100%",
        overflowX: "auto",
        border: `1px solid ${THEME.auditTable.borderColor}`,
        borderRadius: THEME.borderRadius.md,
        backgroundColor: THEME.colors.neutral.light.surface,
      }}
    >
      <table
        style={{
          width: "100%",
          borderCollapse: "separate",
          borderSpacing: 0,
          fontFamily: THEME.fonts.primary,
        }}
      >
        <thead>
          <tr>
            <th style={headerCellStyle}>Vendor</th>
            <th style={headerCellStyle}>Status</th>
            <th style={headerCellStyle}>Trigger</th>
            <th style={headerCellStyle}>Started</th>
            <th style={headerCellStyle}>Duration</th>
            <th style={headerCellStyle}>Bytes</th>
            <th style={headerCellStyle}>Error</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((r, idx) => {
            const rowBg =
              idx % 2 === 0
                ? THEME.auditTable.zebraLight
                : THEME.auditTable.zebraMain
            const statusColor =
              STATUS_COLORS[r.status] ?? THEME.colors.neutral.light.textSecondary
            return (
              <tr key={r.id} style={{ backgroundColor: rowBg }}>
                <td style={cellStyle}>{formatVendorName(r.vendor_name)}</td>
                <td style={{ ...cellStyle, color: statusColor, fontWeight: 500 }}>
                  {r.status}
                </td>
                <td style={cellStyle}>{r.trigger_type}</td>
                <td style={cellStyle}>{formatTimestamp(r.started_at)}</td>
                <td style={cellStyle}>{formatDuration(r.started_at, r.completed_at)}</td>
                <td style={cellStyle}>
                  {r.bytes_fetched !== null ? r.bytes_fetched.toLocaleString() : "—"}
                </td>
                <td
                  style={{
                    ...cellStyle,
                    color: THEME.colors.semantic.danger,
                    fontSize: 11,
                    maxWidth: "300px",
                    wordBreak: "break-word",
                  }}
                  title={r.error_message ?? undefined}
                >
                  {r.error_message ?? "—"}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default RunHistoryTable
